import React, {useEffect, useMemo, useState} from 'react';
import {
  StyleSheet,
  View,
  Image,
  Pressable,
  ImageStyle,
  ViewStyle,
  TextStyle,
  useWindowDimensions,
} from 'react-native';
//ui related imports
import LinearGradient from 'react-native-linear-gradient';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Text, Icon} from 'ui-kits';

// navigation related imports
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

//api related imports
import {useSelector, useDispatch} from 'react-redux';
import {
  profileData,
  profileSuccess,
  profileFailure,
} from 'redux/selectors/profileSelector';
import {defaultUserProfile} from 'constants/DefaultValues';
import {
  getProfileDetailsRequest,
  updateProfileDetailsRequest,
  resetMessage,
} from 'redux/slices/myProfileSlice';

import {ViewProfile} from 'screens/my-profile-screen/ViewProfile';
import {UpdateProfile} from 'screens/my-profile-screen/UpdateProfile';

import {ImagePickerModal} from 'components';
import {useToast} from 'hooks';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';

type propsType = Readonly<{
  mode: 'edit' | 'view';
}> &
  NativeStackScreenProps<RootNavigatorParams>;

type initialValuesType = {
  profile?: File;
  personal_email?: string;
  phone?: string;
};

let initialValues: initialValuesType = {
  personal_email: '',
  phone: '',
};

export function ProfileScreen(props: propsType) {
  const {mode = 'edit', ...rest} = props;

  const dispatch = useDispatch();
  const {showToasts} = useToast();
  const profile = useSelector(profileData);
  const updateProfileSuccess = useSelector(profileSuccess);
  const updateProfileError = useSelector(profileFailure);
  const [open, setOpen] = useState<boolean>(false);

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);
  const {width} = useWindowDimensions();

  const responsiveFormStyle = useMemo(() => {
    if (width <= 640) {
      return {
        ...styles.form,
        flex: mode === 'view' ? 3 : 2,
      };
    }
    return styles.form;
  }, [width, mode, styles.form]);
  // error message toast
  useEffect(() => {
    if (updateProfileError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(updateProfileError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, updateProfileError]);

  // success message toast
  useEffect(() => {
    if (updateProfileSuccess) {
      if (updateProfileSuccess === 'S-10071') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(updateProfileSuccess as SuccessCodes),
          'success',
        );
        rest.navigation.navigate(Routes.VIEW_PROFILE);
      }
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, updateProfileSuccess, rest.navigation]);

  useEffect(() => {
    dispatch(getProfileDetailsRequest());
  }, [dispatch]);

  //dispatching update api request for phone and email
  const handleSubmit = ({phone, personal_email}: initialValuesType) => {
    const payload = {phone, personal_email};
    dispatch(updateProfileDetailsRequest(payload as any));
  };

  //dispatching update api request for profile change
  const handleProfileChange = (image: File) => {
    if (!image) {
      return;
    }
    const payload = new FormData();
    payload.append('profile', image);
    dispatch(updateProfileDetailsRequest(payload as any));
  };

  initialValues = useMemo(
    () => ({
      personal_email: profile?.personal_email!,
      phone: profile?.phone!,
    }),
    [profile],
  );

  return (
    <View style={styles.container}>
      <LinearGradient
        style={styles.banner}
        colors={['#7366FF', '#B961FF']}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}>
        <Image
          resizeMode="contain"
          style={styles.watermark}
          source={require('assets/images/watermark_logo.png')}
        />
        <View style={styles.profile}>
          <View>
            <Image
              resizeMode="cover"
              source={{
                uri: `${
                  profile?.profile ?? defaultUserProfile
                }?${new Date().getTime()}`,
              }}
              style={styles.profileImage}
            />
            <Pressable onPress={() => setOpen(true)} style={styles.camera}>
              <Icon size={28} type="camera_blue" />
            </Pressable>
          </View>
          <View style={styles.basicDetails}>
            <Text style={styles.details} variant="inter24">
              {profile?.first_name?.concat(' ', profile?.last_name ?? '')}
            </Text>
            <Text style={styles.details} variant="sourcesans14">
              {profile?.emp_id ?? '-'}
            </Text>
          </View>
        </View>
      </LinearGradient>

      <View style={responsiveFormStyle}>
        <Text style={styles.header} variant="header">
          About Me
        </Text>
        {mode === 'view' ? (
          <ViewProfile {...rest} />
        ) : (
          <UpdateProfile
            initialValues={initialValues}
            onSubmit={handleSubmit}
            {...rest}
          />
        )}
      </View>
      <ImagePickerModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleProfileChange}
      />
    </View>
  );
}

type styleTypes = {
  container: ViewStyle;
  profile: ViewStyle;
  form: ViewStyle;
  header: TextStyle;
  details: TextStyle;
  banner: ViewStyle;
  watermark: ImageStyle;
  profileImage: ImageStyle;
  camera: ViewStyle;
  basicDetails: ViewStyle;
};

const baseStyles: styleTypes = StyleSheet.create({
  camera: {
    backgroundColor: colors.palette.white,
    bottom: 0,
    right: 0,
    width: 40,
    height: 40,
    borderRadius: 999,
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: 'rgba(0,0,0,0.20)',
    shadowOffset: {width: 0, height: 3},
    shadowRadius: 4,
  },
  container: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.palette.brandBlue,
    margin: spacing['6'],
    borderRadius: corner_radius['rounded-md'],
    paddingBottom: spacing['4'],
  },
  banner: {
    flex: 1,
    maxHeight: 600,
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  profile: {
    gap: spacing['2'],
    justifyContent: 'center',
    position: 'absolute',
    left: 2 * spacing['8'],
    top: 2 * spacing['8'],
  },
  form: {
    flex: 2,
    paddingHorizontal: spacing['6'],
    paddingVertical: spacing['4'],
    marginLeft: 4 * spacing['14'],
    marginTop: spacing['4'],
    gap: spacing['6'],
  },
  header: {
    color: colors.palette.brandBlue,
  },
  watermark: {
    marginRight: 0,
    width: 300,
    height: 120,
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 99999,
  },
  details: {
    alignSelf: 'center',
  },
  basicDetails: {
    gap: spacing['1'],
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    banner: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: spacing['4'],
    },
    profile: {
      position: 'relative',
      alignItems: 'center',
      top: 0,
      left: 0,
    },
    profileImage: {
      width: 90,
      height: 90,
    },
    watermark: {
      display: 'none',
    },
    details: {
      color: colors.palette.white,
    },
    form: {
      flex: 3,
      marginLeft: 0,
      alignItems: 'center',
    },
    header: {
      display: 'none',
    },
  }),
  480: StyleSheet.create({
    header: {
      display: 'none',
    },
  }),
};
